import { useEffect, useState } from 'react';

import {
    Button,
    ButtonList,
    ButtonVariant,
    DirectionEnum,
    FlexBox,
    FlexItem,
    GapSize,
    IconName,
    IconSize,
    Image,
    Modal,
    ModalBody,
    ModalFooter,
    ModalSize,
    Text,
    TextLevels,
    TextMarkup,
    TypographyBold,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';

import type { MobilePlanType } from '@app-types/plan';

import { PromoBorderedBlock } from '@components/promo-bordered-block';

import { MarketingType } from '@constants/plan';

import { sendUpgradePlanModalViewTracking } from '@offer-wall/helpers';

import { Pepite } from './pepite';

interface UpgradePlanModalProps {
    plan?: MobilePlanType;
    modalOpened: boolean;
    onClose: () => void;
    onAdd: (withSmartphone: boolean) => void;
    isLoading: boolean;
}
type ClickedCta = 'with-smartphone' | 'without-smartphone' | null;

const config = getAppConfig();
const certifieCircleWebImage = `${config.assets.cmsImages.url}/original/image/CMS/ECOMMERCE/Cover_modale_Desktop.webp`;
const certifieCircleMobileImage = `${config.assets.cmsImages.url}/original/image/CMS/ECOMMERCE/Cover_modale_Mobile.webp`;

export function UpgradePlanModal({ plan, modalOpened, onClose, onAdd, isLoading }: Readonly<UpgradePlanModalProps>) {
    const [clickedCta, setClickedCta] = useState<ClickedCta>(null);

    useEffect(() => {
        if (modalOpened) {
            sendUpgradePlanModalViewTracking();
        }
    }, [modalOpened]);

    return (
        <Modal
            title={`Un téléphone avec votre ${plan?.sexyName ?? 'forfait'}\u00A0?`}
            active={modalOpened}
            onClose={onClose}
            data-cy='upgrade-plan-modal'
            size={ModalSize.MEDIUM}
        >
            <ModalBody>
                <FlexBox gap={GapSize.FIVE}>
                    <FlexItem>
                        <div className='is-hidden-mobile'>
                            <Image src={certifieCircleWebImage} alt='' width={420} />
                        </div>
                        <div className='is-hidden-tablet is-hidden-desktop'>
                            <Image src={certifieCircleMobileImage} alt='' />
                        </div>
                    </FlexItem>
                    <FlexItem>
                        <FlexBox direction={DirectionEnum.COLUMN} gap={{ mobile: GapSize.TWO, desktop: GapSize.FOUR }}>
                            <PromoBorderedBlock badge='Nouveau' />
                            <Pepite iconSize={IconSize.MEDIUM} iconName={IconName.EUROS} contentType='blocks'>
                                <Text level={TextLevels.TWO}>
                                    <b>Prix réduit</b>&nbsp;avec la reprise mobile de votre ancien téléphone
                                </Text>
                            </Pepite>
                            <Pepite
                                iconSize={IconSize.MEDIUM}
                                iconName={IconName.CREDIT_CARD_ACCEPTED}
                                contentType='blocks'
                            >
                                <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} level={TextLevels.TWO}>
                                    Paiement en plusieurs fois*
                                </Text>
                            </Pepite>

                            {plan?.mirrorPlans?.swappy &&
                            !plan.details?.discounts?.some((d) =>
                                d.marketingTypes.includes(MarketingType.MULTILINE),
                            ) ? (
                                <Pepite
                                    iconSize={IconSize.MEDIUM}
                                    iconName={IconName.SMART_CHANGE}
                                    contentType='blocks'
                                >
                                    <Text level={TextLevels.TWO}>
                                        <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                            Téléphone changeable dès {plan.mirrorPlans?.swappy?.renewDelayLabel}
                                        </Text>{' '}
                                        avec Smartchange
                                    </Text>
                                </Pepite>
                            ) : (
                                <Pepite
                                    iconSize={IconSize.MEDIUM}
                                    iconName={IconName.LIGHTBULB_ON}
                                    contentType='blocks'
                                >
                                    <Text level={TextLevels.TWO}>
                                        <b>Prêt d&apos;un modèle équivalent</b>&nbsp;en cas de perte ou de vol
                                    </Text>
                                </Pepite>
                            )}
                            <Text level={TextLevels.TWO}>
                                Forfait{' '}
                                {plan?.mirrorPlans?.swappy?.obligationLabel ??
                                    plan?.mirrorPlans?.fam?.obligationLabel ??
                                    'sans engagement'}{' '}
                                avec un téléphone.
                            </Text>
                        </FlexBox>
                        <Text level={TextLevels.FOUR}>
                            *Attention&nbsp;! Un crédit coûte de l&apos;argent et doit être remboursé.
                        </Text>
                    </FlexItem>
                </FlexBox>
            </ModalBody>
            <ModalFooter>
                <ButtonList>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        loading={isLoading && clickedCta === 'without-smartphone'}
                        disabled={isLoading && clickedCta !== 'without-smartphone'}
                        onClick={() => {
                            setClickedCta('without-smartphone');
                            onAdd(false);
                        }}
                        data-cy='cta-add-to-cart-without-phone'
                    >
                        Continuer sans téléphone
                    </Button>
                    <Button
                        variant={ButtonVariant.CONVERSION}
                        loading={isLoading && clickedCta === 'with-smartphone'}
                        disabled={isLoading}
                        data-cy='cta-go-to-phonewall'
                        onClick={() => {
                            setClickedCta('with-smartphone');
                            onAdd(true);
                        }}
                    >
                        Voir les téléphones
                    </Button>
                </ButtonList>
            </ModalFooter>
        </Modal>
    );
}
